import * as _ from 'lodash';
import type Stripe from 'stripe';

import {
  fossaPlanMap,
  isBusinessTier,
  isDynamicPlan,
  isEnterpriseTier,
  isFreeTier,
  PREMIUM_PER_REVISION_PLAN_NAME,
} from '../../../../shared/subscriptions';
import { prettyNumber } from '../../../../shared/utils/numbers';
import { window } from '../../../globals';
import { dateFormatter } from '../../../helpers/date';
import { capitalizeWord } from '../../../utils';
import businessTierFeatures from '../billing/features/businessFeatures';
import enterpriseTierFeatures from '../billing/features/enterpriseFeatures';
import freeTierFeatures from '../billing/features/freeFeatures';
import type { StripePriceLimitType } from '../../../../shared/model-typings/stripepricelimits';
import type { FossaSubscriptionPlan, Organization } from '../../../types';
import type { FullTierDescription } from '../billing/features/consts';

import type { ViewIntervalOptions } from './plans';
import type { ChurnFormData } from './plans/ChurnFormModal';

export const isFreePlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'FREE';
export const isBusinessPlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'BUSINESS';
export const isEnterprisePlan = (product: Stripe.Product): boolean => product?.metadata?.type === 'ENTERPRISE';
export const getLimitDisplayValue = (value: string | undefined) => value ? capitalizeWord(value) : '-';
export const planIcon = (type?: StripePriceLimitType) => {
  switch (type) {
    case 'FREE':
      return '/img/icon-user.svg';
    case 'BUSINESS':
      return '/img/icon-briefcase.svg';
    case 'ENTERPRISE':
      return '/img/icon-building.svg';
    default:
      return '';
  }
};

/**
 * Formats a given price to a string representation in monthly terms.
 *
 * @param {number} price - The price to be formatted (in cents).
 * @param {ViewIntervalOptions} viewInterval - The interval option to view the price.
 *        If 'year', the price is converted from yearly to monthly.
 * @returns {string} The formatted price string in the format `$<amount>/month`.
 */
export const formatPrice = (price: number, viewInterval: ViewIntervalOptions): string => {
  // for decimal conversion
  let divisor = 100;

  // for yearly to monthly conversion
  if (viewInterval === 'year') {
    divisor *= 12;
  }

  const amount = Math.round(price / divisor);
  return `$${prettyNumber(amount)}/month`;
};

/**
 * Calculate the discount percentage between the original and new value
 *
 * @param {number} orgValue - The original value before discount
 * @param {number} newValue - The value after discount has been applied
 * @returns {number} The discount percentage
 */
export const calculateDiscountPercentage = (orgValue: number, newValue: number): number => {
  if (orgValue < 1) {
    return 0;
  }
  const discount = orgValue - newValue;

  // The + converts the string from toFixed(2) back to a number
  return +((discount / orgValue) * 100).toFixed(2);
};

/**
 * Generates a full tier description based on the given Stripe price.
 *
 * This function adds dynamic values from Stripe metadata to static price information,
 * ensuring that the tier description reflects any custom limits provided in the metadata.
 *
 * @param {Stripe.Price} price - The Stripe price object containing metadata and product information.
 * @returns {FullTierDescription} The complete tier description with customized main features.
 */
export const dynamicPlanFeatures = (price: Stripe.Price): FullTierDescription => {
  let features: FullTierDescription;

  if (isBusinessPlan(price.product as Stripe.Product)) {
    features = { ...businessTierFeatures };
  } else if (isEnterprisePlan(price.product as Stripe.Product)) {
    features = { ...enterpriseTierFeatures };
  } else {
    features = { ...freeTierFeatures };
  }

  const product = (price?.product as Stripe.Product);

  features.mainFeatures.projectsFeature = getLimitDisplayValue(product.metadata?.limit_projects);
  features.mainFeatures.releaseGroupsFeature = getLimitDisplayValue(product.metadata?.limit_release_groups);
  features.mainFeatures.contributingDevelopers = getLimitDisplayValue(product.metadata?.limit_contributors);
  features.sbomFeatures.sbomImportFeature = getLimitDisplayValue(product.metadata?.limit_projects);
  return features;
};

/**
 * Determines type of the current plan for the given organization
 * @param {Organization} org - The organization to extract the plan type from
 */
export const getCurrentPlanType = (org: Organization): StripePriceLimitType => {
  if (org.stripePriceLimit?.type) {
    return org.stripePriceLimit.type;
  }

  if (isFreeTier({
    plan: org.stripe_plan,
    stripePriceLimit: org.stripePriceLimit,
  })) {
    return 'FREE';
  }

  if (isBusinessTier({
    plan: org.stripe_plan,
    stripePriceLimit: org.stripePriceLimit,
  })) {
    return 'BUSINESS';
  }

  if (isEnterpriseTier({
    plan: org.stripe_plan,
    stripePriceLimit: org.stripePriceLimit,
  })) {
    return 'ENTERPRISE';
  }

  throw new Error(`Unsupported plan: ${org.stripe_plan}`);
};

/**
 * Extracts the bucket size label from a product name string.
 * The bucket size label is expected to be enclosed in square brackets `[]`.
 *
 * @param {string} productName - The name of the Business product, potentially containing a bucket size label in square brackets.
 * @returns {string} - The extracted bucket size label if found; otherwise, the original product name.
 *
 * @example
 * // Example 1: Product name contains a bucket size label
 * const label1 = getBusinessBucketSizeLabel("Business [XL]");
 * console.log(label1); // Output: "XL"
 *
 * // Example 2: Product name does not contain a bucket size label
 * const label2 = getBusinessBucketSizeLabel("Business");
 * console.log(label2); // Output: "Business"
 */
export const getBusinessBucketSizeLabel = (productName: string): string => {
  const match = productName.match(/\[([^\]]+)]/);
  return (match && match[1]) ? match[1] : productName;
};

export const generateSteps = (prices: Stripe.Price[]) => {
  return prices
    .map((price) => {
      return {
        label: getBusinessBucketSizeLabel((price.product as Stripe.Product).name),
        value: price.id,
      };
    });
};

export const convertSize = (size: string): string => {
  const sizeMap: Record<string, string> = {
    XS: 'Extra Small',
    S: 'Small',
    M: 'Medium',
    L: 'Large',
    XL: 'Extra Large',
  };

  return sizeMap[size.toUpperCase()] || '';
};

/**
 * Retrieves the plan label from a Stripe product.
 * The plan label is determined by taking the first word of the product's name.
 *
 * @param {Stripe.Product} product - The Stripe product object containing the name of the product.
 * @returns {string} - The first word of the product name, representing the plan label.
 *
 * @example
 * // Example 1: Product name has multiple words
 * const product1 = { name: "Business [XS]" } as Stripe.Product;
 * const label1 = getPlanLabel(product1);
 * console.log(label1); // Output: "Business"
 *
 * // Example 2: Product name has a single word
 * const product2 = { name: "Free" } as Stripe.Product;
 * const label2 = getPlanLabel(product2);
 * console.log(label2); // Output: "Free"
 */
export const getPlanLabel = (product: Stripe.Product): string => {
  return product.name.split(' ')?.[0] || '';
};

export const getSubscriptionPlanName = (data: FossaSubscriptionPlan): string => {
  // Special handling for organizations with custom billing enabled
  if (data.custom_billing) {
    return 'Enterprise';
  }

  if (isDynamicPlan(data.plan)) {
    return getDynamicSubscriptionPlanName(data?.price?.product as Stripe.Product);
  }

  const mappedPlanInfo = fossaPlanMap[data.plan as keyof typeof fossaPlanMap];
  if (mappedPlanInfo) {
    return `${mappedPlanInfo.label}: ${mappedPlanInfo.description}`;
  }

  return '-';
};

export const getDynamicSubscriptionPlanName = (product: Stripe.Product) => {
  if (isBusinessPlan(product)) {
    return `${getPlanLabel(product)}: ${convertSize(getBusinessBucketSizeLabel(product.name))}`;
  }
  return product?.name || '';
};

export const getSubscriptionPlanDescription = (data: FossaSubscriptionPlan): string => {
  if (data.custom_billing) {
    return 'For organizations that need advanced security & compliance automation at scale';
  }
  if (!data?.price?.product) {
    return '';
  }
  const product = data?.price?.product as Stripe.Product;
  return product?.description || '';
};

export const getUsage = (data: FossaSubscriptionPlan) => {
  if (data.plan === PREMIUM_PER_REVISION_PLAN_NAME) {
    if (!data.usage) {
      return 'There is no billing term for this organization. Please contact your account manager.';
    }

    const planLimit = data.usage.quantity;
    const usage = data.usage.usage;

    const termStart = dateFormatter.format(data.usage.termStart);
    const termEnd = dateFormatter.format(data.usage.termEnd);
    return `${usage} of ${planLimit} revisions used. (${termStart} - ${termEnd})`;
  }

  // Otherwise return contributor usage.
  /**
   * TODO: this seems to be incorrect because contributor_count includes the limit rather than representing the actual usage!
   * @see https://github.com/fossas/FOSSA/blob/4d32f6455b3345fb8d28284e223eeaafb58e7b5a/modules/subscriptions/SubscriptionManager.ts#L437
   */
  const contributorCount = data.contributor_count;
  const contributorLimit = getContributorsLimit();
  if (contributorLimit < 1) {
    return `0 code contributor tracked`;
  }
  if (contributorCount > contributorLimit) {
    return `${contributorLimit}+ code contributors tracked`;
  }

  return `${contributorCount} code contributor${contributorCount !== 1 ? 's' : ''} tracked`;
};

const getContributorsLimit = (): number => {
  return window.user?.organization?.stripePriceLimit?.limits?.contributorCount || 150;
};

export const getDisplayPlanPrice = (data: FossaSubscriptionPlan) => {
  if (data.custom_billing) {
    return 'custom';
  }
  let price;
  if ((data?.trialDaysRemaining || 0) > 0) {
    price = `$${prettyNumber(0)} (trial)`;
  } else {
    const interval = data?.price?.recurring?.interval || 'month';
    price = `$${prettyNumber(data.cost)}/${interval}`;
  }
  return price;
};

export const getDisplayPlanType = (data: FossaSubscriptionPlan) => {
  if (data.custom_billing) {
    return 'custom';
  }
  return formatIntervalType(data?.price?.recurring?.interval as ViewIntervalOptions);
};

export const formatIntervalType = (v?: 'month' | 'year') => v === 'year' ? 'Yearly' : 'Monthly';

export const getDynamicPlanName = (product: Stripe.Product) => {
  if (isBusinessPlan(product)) {
    return `${getPlanLabel(product)} [${convertSize(getBusinessBucketSizeLabel(product.name))}]`;
  }
  return product?.name || '';
};

/**
 * Validates the form to ensure all required questions are answered.
 * If the user selects "Other" as a response, they must provide a reason.
 *
 * @param {ChurnFormData} data - Array of survey responses
 * @returns {boolean} - Returns `true` if the form passes validation, otherwise `false`.
 */
export const isChurnFormValid = (data: ChurnFormData): boolean => {
  if (data.reasons.size < 1 || data.mostValuableFeatures.size < 1) {
    return false;
  }

  if (data.reasons.has('other') && _.isEmpty(data.reasonsNote.trim())) {
    return false;
  }

  if (data.mostValuableFeatures.has('other') && _.isEmpty(data.mostValuableFeaturesNotes.trim())) {
    return false;
  }

  if (data.switchTool && _.isEmpty(data.switchToolNotes.trim())) {
    return false;
  }

  return true;
};
